/**
 * @file AdherentsCrossfitChistera.tsx
 * @summary Article de blog — AdherentsCrossfitChistera
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/adherents-crossfit-chistera";
const TITLE = "Les profils d'adhérents CrossFit Chistera : qui s'entraîne ici ?";
const DESC = "Cadres, parents, anciens sportifs, retraités : panorama des adhérents CrossFit Chistera et de ce que chacun vient chercher à la box.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Faut-il être en forme pour commencer ?", a: "Non. La majorité des nouveaux adhérents ne pratiquaient plus depuis des années. Le programme débutant est fait pour ça." },
  { q: "Y a-t-il un âge max ?", a: "Non. On a des adhérents de plus de 65 ans qui s'entraînent régulièrement. Voir notre article CrossFit après 60 ans." },
  { q: "Est-ce que c'est trop compétitif ?", a: "Pas du tout. La grande majorité ne fait jamais de compétition. Pour ceux qui veulent, on accompagne — mais ce n'est pas la culture par défaut." },
  { q: "Peut-on s'inscrire en couple ou en famille ?", a: "Oui, c'est même fréquent. Voir l'article CrossFit en couple débutant et CrossFit pour enfants." },
];

export default function AdherentsCrossfitChistera() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Les profils d'adhérents CrossFit Chistera"
      breadcrumbLabel="Profils d'adhérents"
      category="Profils · Communauté"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-26" readingMinutes={9}
      ctaVariant="debutant"
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-26", author, reviewer, faq })}
    >
      <EnBref>
        <p>Une box, ce n'est pas un public unique. Chez Chistera, on retrouve <strong>des cadres, des parents, des anciens sportifs, des retraités, des adolescents, des couples</strong>. Chacun vient pour des raisons différentes — santé, performance, lien social, retour de blessure. Le commun : un cadre encadré, des coachs présents, et l'envie de bouger sérieusement.</p>
      </EnBref>

      <h2>Le cadre actif (30-50 ans)</h2>
      <p>C'est le profil le plus représenté. Métier prenant, sédentarité, stress. Vient pour <strong>décompresser, rester en forme, dormir mieux</strong>. Souvent 3 séances par semaine, créneaux 6h30, 12h ou 19h. Voir notre article dédié <Link to="/blog/sport-apres-35-ans-cadre-actif">"Sport après 35 ans : cadre actif"</Link>.</p>

      <h2>Le parent qui veut tenir la cadence</h2>
      <p>Concilier travail, enfants, et garder du temps pour soi. Le créneau midi ou le 6h30 du matin sont les plus fréquents. Vient pour <strong>l'énergie au quotidien</strong> et <strong>donner l'exemple aux enfants</strong>. Souvent inscrit en parallèle au programme <Link to="/blog/crossfit-enfants">CrossFit Enfants</Link>.</p>

      <h2>L'ancien sportif qui reprend</h2>
      <p>A pratiqué rugby, foot, judo, danse... a arrêté il y a 5, 10, 15 ans. Vient retrouver <strong>l'intensité</strong> et <strong>l'esprit d'équipe</strong>. C'est souvent le profil qui progresse le plus vite. Voir <Link to="/blog/ancien-sportif-crossfit">"Ancien sportif : reprendre par le CrossFit"</Link>.</p>

      <h2>Le retour de blessure ou d'opération</h2>
      <p>Vient avec une histoire : LCA, épaule opérée, lombalgie chronique. Cherche un cadre qui adapte sans infantiliser. Le programme <Link to="/reprendre-le-sport">Reprendre le sport</Link> et le <Link to="/blog/coaching-individuel-objectifs-sportifs">coaching individuel</Link> répondent à ce besoin.</p>

      <h2>Le retraité qui veut rester actif</h2>
      <p>60+ ans, en forme ou en reprise. Vient pour <strong>la santé, l'autonomie, le lien social</strong>. Souvent surpris de voir qu'il peut faire plus qu'il ne pensait. Voir <Link to="/blog/crossfit-senior">"CrossFit après 60 ans"</Link>.</p>

      <h2>Le couple qui s'entraîne ensemble</h2>
      <p>Cadre commun, motivation partagée, plus de discussions intéressantes le soir. Voir <Link to="/blog/crossfit-couple-debutant">"CrossFit en couple débutant"</Link>.</p>

      <h2>Le futur compétiteur</h2>
      <p>Minoritaire mais bien présent. Vise l'Open, HYROX, ATHX, ou des compétitions régionales. Suivi en <Link to="/blog/coaching-individuel-competition-hyrox-athx-crossfit">coaching individuel compétition</Link>.</p>

      <h2>Ce qui fait que ça marche ensemble</h2>
      <p>Tout ce monde se croise dans les mêmes créneaux. Le compétiteur côtoie le débutant, le retraité encourage le cadre stressé, le parent explique le scaling au nouveau. C'est <strong>ça, l'esprit Chistera</strong>. Voir <Link to="/blog/competiteurs-crossfit-chistera-debutants">"Compétiteurs et débutants dans la même box"</Link>.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Avis CrossFit Chistera", to: "/blog/avis-crossfit-chistera" },
        { label: "Comment se passe une séance de CrossFit", to: "/blog/comment-se-passe-une-seance-crossfit" },
        { label: "Que recherchez-vous dans le CrossFit ?", to: "/blog/que-recherchez-vous-dans-le-crossfit" },
        { label: "Les coachs et leurs spécialités", to: "/blog/coachs-crossfit-chistera-specialites" },
      ]} />

      <SourcesSection items={[
        { label: "INSERM — Activité physique : prévention et thérapeutique", href: "https://www.inserm.fr/expertise-collective/activite-physique-prevention-traitement-maladies-chroniques/" },
      ]} />
    </ArticleLayout>
  );
}
